import { AUTOMATION_BUILDER_EXAMPLE_PROMPT_LIMIT } from '@reach/shared-types/platforms';
import type { Dispatch, SetStateAction } from 'react';
import { Button } from '../../../../components/ui/button';
import { Card } from '../../../../components/ui/card';
import styles from './AutomationBuilderExamplePrompts.module.css';
import { EXAMPLE_PROMPTS_COPY } from './constants';
import { ExamplePromptRow } from './ExamplePromptRow';
import { ExamplePromptsPreview } from './ExamplePromptsPreview';
import type { ExamplePromptDraft } from './types';

interface AutomationBuilderExamplePromptsProps {
  drafts: ExamplePromptDraft[];
  setDrafts: Dispatch<SetStateAction<ExamplePromptDraft[]>>;
  showErrors: boolean;
  isSaving: boolean;
}

const emptyDraft = (): ExamplePromptDraft => ({ id: crypto.randomUUID(), title: '', prompt: '' });

/**
 * Partner-facing editor for the example prompts shown as chips in the tenant AI Automation
 * Builder. Rendered on Engage › Settings.
 */
export const AutomationBuilderExamplePrompts = ({
  drafts,
  setDrafts,
  showErrors,
  isSaving,
}: AutomationBuilderExamplePromptsProps) => {
  const draftsExist = drafts.length > 0;
  const atLimit = drafts.length >= AUTOMATION_BUILDER_EXAMPLE_PROMPT_LIMIT;
  const countLabel = `${drafts.length} of ${AUTOMATION_BUILDER_EXAMPLE_PROMPT_LIMIT}`;

  const updateDraft = (id: string, changes: Partial<ExamplePromptDraft>) =>
    setDrafts(previous => previous.map(draft => (draft.id === id ? { ...draft, ...changes } : draft)));

  const swapDrafts = (index: number, otherIndex: number) =>
    setDrafts(previous => {
      const reordered = [...previous];
      [reordered[index], reordered[otherIndex]] = [reordered[otherIndex], reordered[index]];
      return reordered;
    });

  const removeDraft = (id: string) => setDrafts(previous => previous.filter(draft => draft.id !== id));

  const addDraft = () => setDrafts(previous => [...previous, emptyDraft()]);

  return (
    <Card>
      <section className={styles.examplePrompts}>
        <header>
          <h3>
            {EXAMPLE_PROMPTS_COPY.title}
            <span>{EXAMPLE_PROMPTS_COPY.betaBadge}</span>
          </h3>
          <p>{EXAMPLE_PROMPTS_COPY.description}</p>
          <span>{countLabel}</span>
        </header>

        <fieldset className={styles.editor} disabled={isSaving}>
          {draftsExist ? (
            <ol className={styles.promptList}>
              {drafts.map((draft, index) => (
                <ExamplePromptRow
                  key={draft.id}
                  draft={draft}
                  position={index + 1}
                  isFirst={index === 0}
                  isLast={index === drafts.length - 1}
                  showErrors={showErrors}
                  onChange={changes => updateDraft(draft.id, changes)}
                  onMoveUp={() => swapDrafts(index, index - 1)}
                  onMoveDown={() => swapDrafts(index, index + 1)}
                  onRemove={() => removeDraft(draft.id)}
                />
              ))}
            </ol>
          ) : (
            <section className={styles.emptyState}>
              <h4>{EXAMPLE_PROMPTS_COPY.emptyTitle}</h4>
              <p>{EXAMPLE_PROMPTS_COPY.emptyDescription}</p>
              <Button type="button" onClick={addDraft}>
                {EXAMPLE_PROMPTS_COPY.addPrompt}
              </Button>
            </section>
          )}

          {draftsExist && (
            <div className={styles.addPrompt}>
              <Button type="button" variant="outline" disabled={atLimit} onClick={addDraft}>
                {EXAMPLE_PROMPTS_COPY.addPrompt}
              </Button>
              {atLimit && <p>{EXAMPLE_PROMPTS_COPY.limitReached}</p>}
            </div>
          )}
        </fieldset>

        <ExamplePromptsPreview drafts={drafts} />
      </section>
    </Card>
  );
};
